<div class="settings-container">
  <!-- Sidebar Navigation -->
  <aside class="settings-sidebar">
    <div class="sidebar-header">
      <div class="logo-section">
        <i class="fas fa-cog rotating-icon"></i>
        <h2>Settings</h2>
      </div>
      <p class="sidebar-subtitle">Customize your experience</p>
    </div>

    <nav class="settings-nav">
      @for (tab of tabs; track tab.id) {
        <button
          class="nav-item"
          [class.active]="activeTab() === tab.id"
          (click)="setActiveTab(tab.id)"
          [attr.aria-selected]="activeTab() === tab.id"
        >
          <div class="nav-icon">
            <i class="fas {{ tab.icon }}"></i>
          </div>
          <div class="nav-content">
            <span class="nav-label">{{ tab.label }}</span>
            <span class="nav-description">{{ tab.description }}</span>
          </div>
          <i class="fas fa-chevron-right nav-arrow"></i>
        </button>
      }
    </nav>

    <!-- Quick Actions -->
    <div class="sidebar-footer">
      <button class="quick-action save-btn" (click)="saveSettings()" [disabled]="!settingsService.hasUnsavedChanges()">
        <i class="fas fa-save"></i>
        <span>Save Changes</span>
        @if (settingsService.hasUnsavedChanges()) {
          <span class="badge">•</span>
        }
      </button>
      <button class="quick-action reset-btn" (click)="resetSettings()">
        <i class="fas fa-undo"></i>
        <span>Reset All</span>
      </button>
    </div>
  </aside>

  <!-- Main Content -->
  <main class="settings-content">
    <!-- Display Settings -->
    @if (activeTab() === 'display') {
      <section class="settings-section" @fadeIn>
        <header class="section-header">
          <div class="header-icon">
            <i class="fas fa-palette"></i>
          </div>
          <div class="header-text">
            <h3>Display Settings</h3>
            <p>Personalize the look and feel of your workspace</p>
          </div>
        </header>

        <div class="settings-group">
          <h4 class="group-title">Theme</h4>
          <p class="group-description">Choose your preferred color scheme</p>
          <div class="theme-selector">
            <button
              class="theme-option"
              [class.active]="currentSettings.display.theme === 'light'"
              (click)="setTheme('light')"
            >
              <div class="theme-preview light-preview">
                <div class="preview-header"></div>
                <div class="preview-sidebar"></div>
                <div class="preview-content"></div>
              </div>
              <span class="theme-label">
                <i class="fas fa-sun"></i>
                Light
              </span>
            </button>
            <button
              class="theme-option"
              [class.active]="currentSettings.display.theme === 'dark'"
              (click)="setTheme('dark')"
            >
              <div class="theme-preview dark-preview">
                <div class="preview-header"></div>
                <div class="preview-sidebar"></div>
                <div class="preview-content"></div>
              </div>
              <span class="theme-label">
                <i class="fas fa-moon"></i>
                Dark
              </span>
            </button>
            <button
              class="theme-option"
              [class.active]="currentSettings.display.theme === 'system'"
              (click)="setTheme('system')"
            >
              <div class="theme-preview system-preview">
                <div class="preview-header"></div>
                <div class="preview-sidebar"></div>
                <div class="preview-content"></div>
              </div>
              <span class="theme-label">
                <i class="fas fa-laptop"></i>
                System
              </span>
            </button>
          </div>
        </div>

        <div class="settings-group">
          <h4 class="group-title">Wallpaper</h4>
          <p class="group-description">Select a background for each desktop</p>

          <!-- Desktop Selector -->
          <div class="desktop-wallpaper-selector">
            <label class="select-label">Choose Desktop</label>
            <div class="desktop-tabs">
              @for (desktop of desktops; track desktop.id) {
                <button
                  class="desktop-tab"
                  [class.active]="activeWallpaperDesktop() === desktop.ref"
                  (click)="setActiveWallpaperDesktop($any(desktop.ref))"
                >
                  <i class="fas fa-desktop"></i>
                  <span>{{ getDesktopLabel(desktop.ref) }}</span>
                </button>
              }
            </div>
          </div>

          <div class="wallpaper-grid">
            @for (wallpaper of wallpapers; track wallpaper.id) {
              <button
                class="wallpaper-option"
                [class.active]="currentDesktopWallpaper() === wallpaper.url"
                (click)="setWallpaper(wallpaper.url)"
                [style.background-image]="'url(' + wallpaper.preview + ')'"
              >
                <span class="wallpaper-name">{{ wallpaper.name }}</span>
                @if (currentDesktopWallpaper() === wallpaper.url) {
                  <div class="selected-indicator">
                    <i class="fas fa-check"></i>
                  </div>
                }
              </button>
            }
          </div>
        </div>

        <div class="settings-group">
          <h4 class="group-title">Font Size</h4>
          <p class="group-description">Adjust the text size throughout the application</p>
          <div class="font-size-selector">
            @for (size of fontSizes; track size.value) {
              <button
                class="font-option"
                [class.active]="currentSettings.display.fontSize === size.value"
                (click)="setFontSize(size.value)"
              >
                <span class="font-preview" [style.font-size]="size.size">Aa</span>
                <span class="font-label">{{ size.label }}</span>
              </button>
            }
          </div>
        </div>

        <div class="settings-group">
          <h4 class="group-title">Visual Effects</h4>
          <div class="toggle-list">
            <label class="toggle-item">
              <div class="toggle-info">
                <span class="toggle-label">Enable Animations</span>
                <span class="toggle-description">Smooth transitions and effects</span>
              </div>
              <div class="toggle-switch">
                <input
                  type="checkbox"
                  [checked]="currentSettings.display.animationsEnabled"
                  (change)="toggleAnimations($any($event.target).checked)"
                />
                <span class="slider"></span>
              </div>
            </label>
            <label class="toggle-item">
              <div class="toggle-info">
                <span class="toggle-label">Reduced Motion</span>
                <span class="toggle-description">Minimize animations for accessibility</span>
              </div>
              <div class="toggle-switch">
                <input
                  type="checkbox"
                  [checked]="currentSettings.display.reducedMotion"
                  (change)="toggleReducedMotion($any($event.target).checked)"
                />
                <span class="slider"></span>
              </div>
            </label>
          </div>
        </div>
      </section>
    }

    <!-- Desktop Settings -->
    @if (activeTab() === 'desktop') {
      <section class="settings-section" @fadeIn>
        <header class="section-header">
          <div class="header-icon">
            <i class="fas fa-desktop"></i>
          </div>
          <div class="header-text">
            <h3>Desktop Settings</h3>
            <p>Configure desktop behavior and window management</p>
          </div>
        </header>

        <div class="settings-group">
          <h4 class="group-title">Custom Desktop Names</h4>
          <p class="group-description">Give each desktop a custom label (e.g. Work, Personal). Shown in the header and when switching desktops.</p>
          <div class="desktop-labels-grid">
            @for (desktop of desktops; track desktop.id) {
              <div class="desktop-label-row">
                <label [for]="'desktop-label-' + desktop.ref" class="desktop-label-label">{{ desktop.name }}</label>
                <input
                  [id]="'desktop-label-' + desktop.ref"
                  type="text"
                  class="form-input desktop-label-input"
                  [placeholder]="desktop.name"
                  [value]="getDesktopLabelValue(desktop.ref)"
                  (input)="setDesktopLabel(desktop.ref, $any($event.target).value)"
                  maxlength="32"
                />
              </div>
            }
          </div>
        </div>

        <div class="settings-group">
          <h4 class="group-title">Default Desktop</h4>
          <p class="group-description">Choose which desktop to show on startup</p>
          <div class="desktop-selector">
            @for (desktop of desktops; track desktop.id; let num = $index) {
              <button
                class="desktop-option"
                [class.active]="currentSettings.desktop.defaultDesktop === num"
                (click)="setDefaultDesktop(num)"
              >
                <div class="desktop-icon">
                  <i class="fas fa-th-large"></i>
                </div>
                <span>{{ getDesktopLabel(desktop.ref) }}</span>
              </button>
            }
          </div>
        </div>

        <div class="settings-group">
          <h4 class="group-title">Window Behavior</h4>
          <div class="toggle-list">
            <label class="toggle-item">
              <div class="toggle-info">
                <span class="toggle-label">Show Desktop Previews</span>
                <span class="toggle-description">Display thumbnails when switching desktops</span>
              </div>
              <div class="toggle-switch">
                <input
                  type="checkbox"
                  [checked]="currentSettings.desktop.showDesktopPreviews"
                  (change)="toggleDesktopPreviews($any($event.target).checked)"
                />
                <span class="slider"></span>
              </div>
            </label>
            <label class="toggle-item">
              <div class="toggle-info">
                <span class="toggle-label">Double-Click to Maximize</span>
                <span class="toggle-description">Maximize window when double-clicking the title bar</span>
              </div>
              <div class="toggle-switch">
                <input
                  type="checkbox"
                  [checked]="currentSettings.desktop.doubleClickToMaximize"
                  (change)="toggleDoubleClickMaximize($any($event.target).checked)"
                />
                <span class="slider"></span>
              </div>
            </label>
            <label class="toggle-item">
              <div class="toggle-info">
                <span class="toggle-label">Snap Windows to Edges</span>
                <span class="toggle-description">Automatically align windows when dragging to edges</span>
              </div>
              <div class="toggle-switch">
                <input
                  type="checkbox"
                  [checked]="currentSettings.desktop.snapWindowsToEdges"
                  (change)="toggleSnapToEdges($any($event.target).checked)"
                />
                <span class="slider"></span>
              </div>
            </label>
          </div>
        </div>
      </section>
    }

    <!-- Privacy & Security Settings -->
    @if (activeTab() === 'privacy') {
      <section class="settings-section" @fadeIn>
        <header class="section-header">
          <div class="header-icon">
            <i class="fas fa-shield-halved"></i>
          </div>
          <div class="header-text">
            <h3>Privacy & Security</h3>
            <p>Protect your workspace and manage data</p>
          </div>
        </header>

        <div class="settings-group">
          <h4 class="group-title">Lock Screen</h4>
          <div class="toggle-list">
            <label class="toggle-item">
              <div class="toggle-info">
                <span class="toggle-label">Auto-Lock</span>
                <span class="toggle-description">Automatically lock after period of inactivity</span>
              </div>
              <div class="toggle-switch">
                <input
                  type="checkbox"
                  [checked]="currentSettings.privacy.autoLockEnabled"
                  (change)="toggleAutoLock($any($event.target).checked)"
                />
                <span class="slider"></span>
              </div>
            </label>
          </div>

          @if (currentSettings.privacy.autoLockEnabled) {
            <div class="select-wrapper">
              <label class="select-label">Auto-Lock Timeout</label>
              <select
                class="settings-select"
                [value]="currentSettings.privacy.autoLockTimeout"
                (change)="setAutoLockTimeout(+$any($event.target).value)"
              >
                @for (option of autoLockOptions; track option.value) {
                  <option [value]="option.value">{{ option.label }}</option>
                }
              </select>
            </div>
          }

          <button class="action-button primary" (click)="lockNow()">
            <i class="fas fa-lock"></i>
            Lock Now
          </button>
        </div>

        <div class="settings-group">
          <h4 class="group-title">Privacy Options</h4>
          <div class="toggle-list">
            <label class="toggle-item">
              <div class="toggle-info">
                <span class="toggle-label">Show Notifications on Lock Screen</span>
                <span class="toggle-description">Display notification previews when locked</span>
              </div>
              <div class="toggle-switch">
                <input
                  type="checkbox"
                  [checked]="currentSettings.privacy.showNotificationsOnLockScreen"
                  (change)="toggleLockScreenNotifications($any($event.target).checked)"
                />
                <span class="slider"></span>
              </div>
            </label>
            <label class="toggle-item">
              <div class="toggle-info">
                <span class="toggle-label">Remember Open Apps</span>
                <span class="toggle-description">Restore your session after refresh</span>
              </div>
              <div class="toggle-switch">
                <input
                  type="checkbox"
                  [checked]="currentSettings.privacy.rememberOpenApps"
                  (change)="toggleRememberApps($any($event.target).checked)"
                />
                <span class="slider"></span>
              </div>
            </label>
            <label class="toggle-item">
              <div class="toggle-info">
                <span class="toggle-label">Clear Data on Logout</span>
                <span class="toggle-description">Remove all cached data when signing out</span>
              </div>
              <div class="toggle-switch">
                <input
                  type="checkbox"
                  [checked]="currentSettings.privacy.clearDataOnLogout"
                  (change)="toggleClearDataOnLogout($any($event.target).checked)"
                />
                <span class="slider"></span>
              </div>
            </label>
          </div>
        </div>

        <div class="settings-group">
          <h4 class="group-title">Storage</h4>
          <p class="group-description">Manage your local data storage</p>
          <div class="storage-info">
            <div class="storage-bar">
              <div class="storage-used" [style.width.%]="storagePercentage()"></div>
            </div>
            <div class="storage-stats">
              <span>{{ storageUsed() }} used of {{ storageTotal() }}</span>
              <span class="storage-percentage">{{ storagePercentage() }}%</span>
            </div>
          </div>
          <button class="action-button danger" (click)="clearStorage()">
            <i class="fas fa-trash"></i>
            Clear Cached Data
          </button>
        </div>
      </section>
    }

    <!-- Notification Settings -->
    @if (activeTab() === 'notifications') {
      <section class="settings-section" @fadeIn>
        <header class="section-header">
          <div class="header-icon">
            <i class="fas fa-bell"></i>
          </div>
          <div class="header-text">
            <h3>Notifications</h3>
            <p>Configure how you receive alerts and updates</p>
          </div>
        </header>

        <div class="settings-group">
          <h4 class="group-title">General</h4>
          <div class="toggle-list">
            <label class="toggle-item">
              <div class="toggle-info">
                <span class="toggle-label">Enable Notifications</span>
                <span class="toggle-description">Receive alerts from applications</span>
              </div>
              <div class="toggle-switch">
                <input
                  type="checkbox"
                  [checked]="currentSettings.notifications.enabled"
                  (change)="toggleNotifications($any($event.target).checked)"
                />
                <span class="slider"></span>
              </div>
            </label>
            <label class="toggle-item">
              <div class="toggle-info">
                <span class="toggle-label">Notification Sounds</span>
                <span class="toggle-description">Play audio when notifications arrive</span>
              </div>
              <div class="toggle-switch">
                <input
                  type="checkbox"
                  [checked]="currentSettings.notifications.sound"
                  (change)="toggleNotificationSound($any($event.target).checked)"
                />
                <span class="slider"></span>
              </div>
            </label>
            <label class="toggle-item">
              <div class="toggle-info">
                <span class="toggle-label">Badge Count</span>
                <span class="toggle-description">Show unread count on app icons</span>
              </div>
              <div class="toggle-switch">
                <input
                  type="checkbox"
                  [checked]="currentSettings.notifications.badgeCount"
                  (change)="toggleBadgeCount($any($event.target).checked)"
                />
                <span class="slider"></span>
              </div>
            </label>
            <label class="toggle-item">
              <div class="toggle-info">
                <span class="toggle-label">Preview Text</span>
                <span class="toggle-description">Show message content in notifications</span>
              </div>
              <div class="toggle-switch">
                <input
                  type="checkbox"
                  [checked]="currentSettings.notifications.previewText"
                  (change)="togglePreviewText($any($event.target).checked)"
                />
                <span class="slider"></span>
              </div>
            </label>
          </div>
        </div>

        <div class="settings-group">
          <h4 class="group-title">Do Not Disturb</h4>
          <div class="toggle-list">
            <label class="toggle-item">
              <div class="toggle-info">
                <span class="toggle-label">Enable Do Not Disturb</span>
                <span class="toggle-description">Silence all notifications</span>
              </div>
              <div class="toggle-switch">
                <input
                  type="checkbox"
                  [checked]="currentSettings.notifications.doNotDisturb"
                  (change)="toggleDoNotDisturb($any($event.target).checked)"
                />
                <span class="slider"></span>
              </div>
            </label>
          </div>

          @if (currentSettings.notifications.doNotDisturb) {
            <div class="schedule-picker">
              <div class="time-input">
                <label>Start Time</label>
                <input
                  type="time"
                  [value]="currentSettings.notifications.doNotDisturbStart"
                  (change)="setDoNotDisturbSchedule($any($event.target).value, currentSettings.notifications.doNotDisturbEnd)"
                />
              </div>
              <div class="time-separator">
                <i class="fas fa-arrow-right"></i>
              </div>
              <div class="time-input">
                <label>End Time</label>
                <input
                  type="time"
                  [value]="currentSettings.notifications.doNotDisturbEnd"
                  (change)="setDoNotDisturbSchedule(currentSettings.notifications.doNotDisturbStart, $any($event.target).value)"
                />
              </div>
            </div>
          }
        </div>
      </section>
    }

    <!-- Accessibility Settings -->
    @if (activeTab() === 'accessibility') {
      <section class="settings-section" @fadeIn>
        <header class="section-header">
          <div class="header-icon">
            <i class="fas fa-universal-access"></i>
          </div>
          <div class="header-text">
            <h3>Accessibility</h3>
            <p>Make the workspace easier to see and use</p>
          </div>
        </header>

        <div class="settings-group">
          <h4 class="group-title">Vision</h4>
          <div class="toggle-list">
            <label class="toggle-item">
              <div class="toggle-info">
                <span class="toggle-label">High Contrast</span>
                <span class="toggle-description">Increase color contrast for better visibility</span>
              </div>
              <div class="toggle-switch">
                <input
                  type="checkbox"
                  [checked]="currentSettings.accessibility.highContrast"
                  (change)="toggleHighContrast($any($event.target).checked)"
                />
                <span class="slider"></span>
              </div>
            </label>
            <label class="toggle-item">
              <div class="toggle-info">
                <span class="toggle-label">Large Text</span>
                <span class="toggle-description">Increase text size throughout the application</span>
              </div>
              <div class="toggle-switch">
                <input
                  type="checkbox"
                  [checked]="currentSettings.accessibility.largeText"
                  (change)="toggleLargeText($any($event.target).checked)"
                />
                <span class="slider"></span>
              </div>
            </label>
          </div>
        </div>

        <div class="settings-group">
          <h4 class="group-title">Navigation</h4>
          <div class="toggle-list">
            <label class="toggle-item">
              <div class="toggle-info">
                <span class="toggle-label">Keyboard Navigation</span>
                <span class="toggle-description">Navigate using Tab and arrow keys</span>
              </div>
              <div class="toggle-switch">
                <input
                  type="checkbox"
                  [checked]="currentSettings.accessibility.keyboardNavigation"
                  (change)="toggleKeyboardNavigation($any($event.target).checked)"
                />
                <span class="slider"></span>
              </div>
            </label>
            <label class="toggle-item">
              <div class="toggle-info">
                <span class="toggle-label">Focus Indicators</span>
                <span class="toggle-description">Show visual focus outline on interactive elements</span>
              </div>
              <div class="toggle-switch">
                <input
                  type="checkbox"
                  [checked]="currentSettings.accessibility.focusIndicators"
                  (change)="toggleFocusIndicators($any($event.target).checked)"
                />
                <span class="slider"></span>
              </div>
            </label>
            <label class="toggle-item">
              <div class="toggle-info">
                <span class="toggle-label">Screen Reader Support</span>
                <span class="toggle-description">Optimize for screen readers</span>
              </div>
              <div class="toggle-switch">
                <input
                  type="checkbox"
                  [checked]="currentSettings.accessibility.screenReader"
                  (change)="toggleScreenReader($any($event.target).checked)"
                />
                <span class="slider"></span>
              </div>
            </label>
          </div>
        </div>
      </section>
    }

    <!-- Language & Region Settings -->
    @if (activeTab() === 'language') {
      <section class="settings-section" @fadeIn>
        <header class="section-header">
          <div class="header-icon">
            <i class="fas fa-globe"></i>
          </div>
          <div class="header-text">
            <h3>Language & Region</h3>
            <p>Set your preferred language and regional formats</p>
          </div>
        </header>

        <div class="settings-group">
          <h4 class="group-title">Language</h4>
          <p class="group-description">Select your preferred display language</p>
          <div class="language-grid">
            @for (lang of languages; track lang.code) {
              <button
                class="language-option"
                [class.active]="currentSettings.language === lang.code"
                (click)="setLanguage(lang.code)"
              >
                <span class="language-flag">{{ lang.flag }}</span>
                <span class="language-name">{{ lang.name }}</span>
                @if (currentSettings.language === lang.code) {
                  <i class="fas fa-check language-check"></i>
                }
              </button>
            }
          </div>
        </div>

        <div class="settings-group">
          <h4 class="group-title">Date & Time</h4>
          <div class="format-options">
            <div class="select-wrapper">
              <label class="select-label">Date Format</label>
              <select
                class="settings-select"
                [value]="currentSettings.dateFormat"
                (change)="setDateFormat($any($event.target).value)"
              >
                @for (format of dateFormats; track format.value) {
                  <option [value]="format.value">{{ format.label }}</option>
                }
              </select>
            </div>

            <div class="time-format-selector">
              <label class="select-label">Time Format</label>
              <div class="format-buttons">
                <button
                  class="format-option"
                  [class.active]="currentSettings.timeFormat === '12h'"
                  (click)="setTimeFormat('12h')"
                >
                  12-hour
                </button>
                <button
                  class="format-option"
                  [class.active]="currentSettings.timeFormat === '24h'"
                  (click)="setTimeFormat('24h')"
                >
                  24-hour
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    }

    <!-- About Section -->
    @if (activeTab() === 'about') {
      <section class="settings-section" @fadeIn>
        <header class="section-header">
          <div class="header-icon">
            <i class="fas fa-info-circle"></i>
          </div>
          <div class="header-text">
            <h3>About EWS Platform</h3>
            <p>Application information and system details</p>
          </div>
        </header>

        <div class="settings-group">
          <div class="about-card">
            <div class="about-logo">
              <img src="/assets/logo.png" alt="EWS logo" title="EWS" width="120" height="40" loading="lazy" />
            </div>
            <div class="about-info">
              <h4>EWS Platform</h4>
              <p class="version">Version {{ systemInfo().appVersion }}</p>
              <p class="build">Build {{ systemInfo().buildNumber }}</p>
            </div>
          </div>

          <div class="system-info">
            <h4 class="group-title">System Information</h4>
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">Platform</span>
                <span class="info-value">{{ systemInfo().platform }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Screen Resolution</span>
                <span class="info-value">{{ systemInfo().screenResolution }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Language</span>
                <span class="info-value">{{ systemInfo().language }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Timezone</span>
                <span class="info-value">{{ systemInfo().timezone }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Device Memory</span>
                <span class="info-value">{{ systemInfo().memoryUsage }}</span>
              </div>
            </div>
          </div>

          <div class="action-buttons">
            <button class="action-button secondary" (click)="checkForUpdates()">
              <i class="fas fa-sync"></i>
              Check for Updates
            </button>
            <button class="action-button secondary" (click)="exportSettings()">
              <i class="fas fa-download"></i>
              Export Settings
            </button>
            <button class="action-button secondary" (click)="importSettings()">
              <i class="fas fa-upload"></i>
              Import Settings
            </button>
          </div>

          <div class="about-links">
            <a href="https://www.5searches.com/privacy-policy/" target="_blank" rel="noopener" class="about-link">
              <i class="fas fa-shield-halved"></i>
              Privacy Policy
            </a>
            <a href="https://www.5searches.com/terms/" target="_blank" rel="noopener" class="about-link">
              <i class="fas fa-file-contract"></i>
              Terms of Service
            </a>
            <a href="https://www.5searches.com/support/" target="_blank" rel="noopener" class="about-link">
              <i class="fas fa-headset"></i>
              Get Support
            </a>
          </div>

          <p class="copyright">
            © {{ now().getFullYear() }} 5Searches Inc. All rights reserved.
          </p>
        </div>
      </section>
    }
  </main>
</div>

